import { Box, Button, Grid, SvgIcon, useMediaQuery } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as LeftChevronIcon } from '../images/chevron-left.svg';
import { colors, theme } from '../utils/theme';
import { useAnalytes } from '../config/api/queries';
import {
  Header,
  AnalyteCard,
  TestImportanceCard,
  SubOptimalSymptoms,
  BloodImprovementPlan,
  PersonalizedCart,
} from '../components/Results/BloodVitaminInsights';
import { RootineDivider } from '../atoms/RootineDivider';
import { Loading, MissingResults, RootineFooter } from '../components';
import { CHECKOUT_URLS, PRODUCT_CODE_MAPPING } from '../config';
import { AnalyteData, BloodAnalyte } from '../types';
import mineralAnalyteData from '../config/blood_mineral_analyte_details.json';
import bloodMineralDetails from '../config/blood_mineral_dashboard_copy.json';
import { ResourcesAndContentTabs } from '../components/Results/StressInsights';
import { TestRenewelInfoCard } from '../components/Results/TestRenewelInfoCard';
import { hasDurationPassedSinceTest } from '../utils';

export const BloodMineralInsights = (): JSX.Element => {
  const navigate = useNavigate();
  const analyteReport = useAnalytes();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const {
    dashboard_header,
    why_vitamins_are_important,
    sub_optimal_symptoms,
    related_content,
  } = bloodMineralDetails;

  const mineralAnalyteDataMap = mineralAnalyteData.reduce((acc, item) => {
    acc[item?.analyte_code as BloodAnalyte] = item;
    return acc;
  }, {} as Record<BloodAnalyte, AnalyteData>);

  if (analyteReport.isLoading) {
    return <Loading fullPage />;
  }

  const mineralAnalytes = analyteReport?.data
    ?.filter(
      (analyte) =>
        analyte?.result_history?.length > 0 && analyte?.test_type !== 'vitamin'
    )
    .sort((a, b) => {
      if (a?.result_history?.length && b?.result_history?.length) {
        return a.sort_order - b.sort_order;
      }
      return b?.result_history?.length - a?.result_history?.length;
    });

  if (!mineralAnalytes || !mineralAnalytes.length) {
    return (
      <Box p={4}>
        <MissingResults
          head={'Missing Blood Mineral Insights'.toLocaleUpperCase()}
          subhead="Take an at-home lab test to inform your Rootine smart formula and gain access to your Blood Mineral insights."
          buttonLabel="Improve my Rootine"
          buttonLink={CHECKOUT_URLS.blood_mineral_test.link}
          foot="Back to dashboard"
          footLink="/dashboard"
          height={80}
        />
        <RootineFooter />
      </Box>
    );
  }

  const latestTestDateStr = mineralAnalytes[0]?.result_history[0]?.date ?? '';
  const shouldShowRenewelInfoCard = hasDurationPassedSinceTest(
    new Date(latestTestDateStr),
    '1 year'
  );

  return (
    <Grid
      container
      alignItems="flex-start"
      flexDirection="column"
      sx={{ px: 2, mt: 6, [theme.breakpoints.down('sm')]: { mt: 8 } }}
    >
      <Button
        variant="text"
        startIcon={
          <SvgIcon
            component={LeftChevronIcon}
            inheritViewBox
            sx={{
              width: 18,
              height: 18,
              fill: colors.greys.BASE_WHITE,
              '&:hover': { fill: colors.greys.BG_WHITE },
            }}
          />
        }
        onClick={() => navigate(-1)}
        sx={{
          fontSize: 14,
          '&:hover': {
            background: 'transparent',
            color: colors.greys.TEXT_BLACK,
            textDecoration: 'underline',
          },
        }}
      >
        Back
      </Button>

      <Grid
        container
        flexDirection="column"
        sx={{ [theme.breakpoints.up('md')]: { px: 8 } }}
      >
        <Header date={latestTestDateStr} title={dashboard_header} />

        {isMobile ? (
          // Mobile Layout
          <Grid container flexDirection="column">
            {shouldShowRenewelInfoCard && (
              <Box sx={{ mt: 2 }}>
                <TestRenewelInfoCard testType="blood" />
              </Box>
            )}
            <Box sx={{ mt: 2 }}>
              <TestImportanceCard cardDetails={why_vitamins_are_important} />
            </Box>
            <Box sx={{ mt: 2 }}>
              <SubOptimalSymptoms details={sub_optimal_symptoms} />
            </Box>
            <Grid item sx={{ mt: 2, background: colors.blues.LIGHT, p: 2 }}>
              {mineralAnalytes.map((analyte) => (
                <Box sx={{ mb: 2 }} key={analyte?.id}>
                  <AnalyteCard
                    analyte={analyte}
                    analyteDataMap={mineralAnalyteDataMap}
                  />
                </Box>
              ))}
            </Grid>
            <Grid item sx={{ mt: 2 }}>
              <BloodImprovementPlan
                recommendations={bloodMineralDetails.recommendations}
              />
            </Grid>
            <Grid item sx={{ mt: 2 }}>
              <ResourcesAndContentTabs
                relatedContent={{
                  title: related_content.title,
                  content: related_content.content,
                }}
              />
            </Grid>
            <Grid item sx={{ mt: 2 }}>
              <PersonalizedCart products={[]} />
            </Grid>
            <Grid item>
              <RootineFooter />
            </Grid>
          </Grid>
        ) : (
          // Desktop Layout
          <Grid container flexDirection="row" gap={4} sx={{ mt: 4 }}>
            {/* Left Side */}
            <Grid container item flexDirection="column" tablet={5}>
              <Grid
                item
                sx={{ background: colors.blues.LIGHT, px: 3, pt: 3, mb: 2 }}
              >
                {mineralAnalytes?.map((analyte) => (
                  <Box sx={{ mb: 3 }} key={analyte?.id}>
                    <AnalyteCard
                      analyte={analyte}
                      analyteDataMap={mineralAnalyteDataMap}
                    />
                  </Box>
                ))}
              </Grid>
              <Grid item>
                <ResourcesAndContentTabs
                  relatedContent={{
                    title: related_content.title,
                    content: related_content.content,
                  }}
                />
              </Grid>
              <Grid item sx={{ mt: 2 }}>
                <PersonalizedCart products={[]} />
              </Grid>
            </Grid>

            <RootineDivider
              dividerProps={{ orientation: 'vertical', flexItem: true }}
            />

            {/* Right Side */}
            <Grid container item flexDirection="column" tablet={6}>
              {shouldShowRenewelInfoCard && (
                <Box sx={{ mb: 4 }}>
                  <TestRenewelInfoCard testType="blood" />
                </Box>
              )}

              <TestImportanceCard cardDetails={why_vitamins_are_important} />

              <Box sx={{ mt: 4 }}>
                <SubOptimalSymptoms details={sub_optimal_symptoms} />
              </Box>
              <Box sx={{ mt: 4 }}>
                <BloodImprovementPlan
                  recommendations={bloodMineralDetails.recommendations}
                />
              </Box>
            </Grid>
            <Grid item>
              <RootineFooter />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
